import {
  BadgeCheck,
  Clock,
  FileText,
  Percent,
  ShieldCheck,
  Wallet,
} from 'lucide-react'
import { useFaqs } from '../hooks/useFaqs'
import { useGsapContext, useGsapStagger } from '../hooks/useGsapReveal'
import {
  DURATION,
  magnetize,
  revealOnScroll,
  staggerChildren,
} from '../animations/gsapAnimations'
import Accordion from '../components/ui/Accordion'
import { RichText } from '../components/ui/RichText'
import Button from '../components/ui/Button'
import PageIntro from '../components/ui/PageIntro'
import Section from '../components/ui/Section'
import SectionHeading from '../components/ui/SectionHeading'
import BankPartners from '../components/finance/BankPartners'
import FinancingSteps from '../components/finance/FinancingSteps'
import LeaseCalculator from '../components/finance/LeaseCalculator'

const BENEFITS = [
  {
    icon: Percent,
    title: 'Competitive markup',
    body: 'Eight banks quote on the same file, Islamic and conventional. We show you every rate that comes back, not just the one that pays us best.',
  },
  {
    icon: Wallet,
    title: 'Down payment from 20%',
    body: 'Structure the deposit around what you have now, and the instalment around what you can carry monthly.',
  },
  {
    icon: Clock,
    title: 'Approval in 48 hours',
    body: 'Complete documents usually clear in two days. Some partners return a decision the same day.',
  },
  {
    icon: FileText,
    title: 'One file, every bank',
    body: 'You fill in one set of papers. We take it to all eight partners rather than sending you to each branch.',
  },
  {
    icon: ShieldCheck,
    title: 'Insurance and tracker handled',
    body: 'Both are bank requirements. We arrange them with the vehicle so nothing holds up delivery.',
  },
  {
    icon: BadgeCheck,
    title: 'Written before signed',
    body: 'Markup, processing fee, insurance and total payable are itemised in your quote before anything is signed.',
  },
] as const

const SALARIED = [
  'CNIC copy (front and back)',
  'Two passport-size photographs',
  'Last three salary slips',
  'Six months of bank statements',
  'Employment or appointment letter',
  'Utility bill as address proof',
] as const

const SELF_EMPLOYED = [
  'CNIC copy (front and back)',
  'Two passport-size photographs',
  'Last two years of tax returns',
  'Twelve months of business bank statements',
  'Business registration or NTN certificate',
  'Utility bill as address proof',
] as const

function DocumentList({
  title,
  items,
}: {
  title: string
  items: readonly string[]
}) {
  return (
    <div data-doc className="rounded-xl border border-line bg-raised p-7 shadow-card sm:p-9">
      <h3 className="font-display text-[19px] font-semibold tracking-[-0.02em] text-platinum">
        {title}
      </h3>
      <ul className="mt-6 flex flex-col gap-3.5">
        {items.map((item) => (
          <li
            key={item}
            data-doc-item
            className="flex items-start gap-3 text-[14.5px] leading-relaxed text-silver"
          >
            <BadgeCheck
              aria-hidden="true"
              strokeWidth={1.6}
              className="mt-0.5 size-4 shrink-0 text-lime-deep"
            />
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

function Financing() {
  const benefitsRef = useGsapStagger<HTMLUListElement>('[data-benefit]', {
    y: 24,
    stagger: 0.07,
  })

  /* The two document lists arrive side by side, then their lines are
     ticked off one at a time — a checklist being read, not a card landing. */
  const documentsRef = useGsapContext<HTMLDivElement>((scope) => {
    staggerChildren(scope, '[data-doc]', { y: 26, stagger: 0.12 })
    staggerChildren(scope, '[data-doc-item]', {
      y: 10,
      stagger: 0.05,
      delay: 0.35,
      duration: DURATION.fast,
      start: 'top 95%',
    })
  })

  /* The same questions the home page carries — the showroom's own, once it
     has filed any, and the built-in list until then. */
  const { faqs } = useFaqs()

  /* The questions land one under the other. */
  const faqRef = useGsapStagger<HTMLDivElement>(':scope > div > *', {
    y: 16,
    stagger: 0.06,
  })

  /* The closing card settles from a hair under full size, and its primary
     action leans toward the cursor. */
  const applyRef = useGsapContext<HTMLDivElement>((scope) => {
    revealOnScroll(scope, { y: 36, scale: 0.98, duration: 1.2 })
    return magnetize(scope)
  })

  return (
    <>
      <PageIntro
        eyebrow="Financing"
        title="Drive your dream car with flexible financing"
        lede="One application, eight partner banks, and every offer laid side by side. You choose the markup and the tenure — we handle the file from submission to delivery."
      >
        <div className="flex flex-wrap gap-3">
          <Button to="/home#cars" size="lg" arrow magnetic>
            Explore cars
          </Button>
          <Button variant="ghost" size="lg" to="/lease-calculator">
            Calculate lease
          </Button>
        </div>
      </PageIntro>

      {/* ---------- process ---------- */}
      <Section tone="ink" labelledBy="process-title">
        <SectionHeading
          id="process-title"
          eyebrow="How it works"
          title="Six steps, one conversation"
          lede="Leasing a car in Pakistan usually means six visits and a folder of photocopies. This is the whole journey."
        />
        <div className="mt-14">
          <FinancingSteps />
        </div>
      </Section>

      {/* ---------- benefits ---------- */}
      <Section
        tone="surface"
        labelledBy="benefits-title"
      >
        <SectionHeading
          id="benefits-title"
          eyebrow="Financing benefits"
          title="What you get by going through us"
        />
        <ul
          ref={benefitsRef}
          className="mt-12 grid gap-px overflow-hidden rounded-xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-3"
        >
          {BENEFITS.map((benefit) => (
            <li
              key={benefit.title}
              data-benefit
              className="group flex flex-col gap-4 bg-raised p-8 transition-colors duration-300 hover:bg-surface"
            >
              <span className="icon-ring size-12 transition duration-300 group-hover:bg-lime group-hover:text-void">
                <benefit.icon
                  aria-hidden="true"
                  strokeWidth={1.6}
                  className="size-5"
                />
              </span>
              <h3 className="font-display text-[18px] font-semibold tracking-[-0.02em] text-platinum">
                {benefit.title}
              </h3>
              <p className="text-[14.5px] leading-relaxed text-steel">
                {benefit.body}
              </p>
            </li>
          ))}
        </ul>
      </Section>

      {/* ---------- documents ---------- */}
      <Section tone="ink" labelledBy="documents-title">
        <SectionHeading
          id="documents-title"
          eyebrow="Required documents"
          title="What to bring"
          lede="Incomplete income proof is the single most common cause of delay. Get these together and approval usually takes two days."
        />
        <div ref={documentsRef} className="mt-12 grid gap-6 lg:grid-cols-2">
          <DocumentList title="If you are salaried" items={SALARIED} />
          <DocumentList title="If you are self-employed" items={SELF_EMPLOYED} />
        </div>
      </Section>

      {/* ---------- calculator ---------- */}
      <Section
        tone="surface"
        labelledBy="financing-calc-title"
      >
        <SectionHeading
          id="financing-calc-title"
          eyebrow="Lease calculator"
          title="Find your monthly number"
          lede="Model the down payment and tenure before you apply, so the offer that comes back is the one you expected."
        />
        <LeaseCalculator variant="compact" className="mt-12" />
      </Section>

      <BankPartners />

      {/* ---------- faqs ---------- */}
      <Section
        id="faq"
        tone="surface"
        labelledBy="faq-title"
      >
        <SectionHeading
          id="faq-title"
          eyebrow="Questions"
          title="Frequently asked"
        />
        <div ref={faqRef} className="mt-10 max-w-4xl">
          <Accordion
            items={faqs.map((faq) => ({
              id: faq.id,
              question: faq.question,
              answer: <RichText html={faq.html} />,
            }))}
            defaultOpenId={faqs[0]?.id}
          />
        </div>
      </Section>

      {/* ---------- apply ---------- */}
      <Section tone="ink" labelledBy="apply-title">
        <div
          ref={applyRef}
          className="relative overflow-hidden rounded-3xl border border-lime/25 bg-raised px-7 py-14 text-center shadow-pop sm:px-14 sm:py-20"
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(720px_320px_at_50%_-30%,rgb(200_245_66/0.14),transparent)]"
          />
          <h2
            id="apply-title"
            className="mx-auto max-w-[20ch] font-display text-display-2 text-platinum"
          >
            Ready to apply?
          </h2>
          <p className="mx-auto mt-5 max-w-[54ch] text-[15.5px] leading-relaxed text-steel">
            Send us the car and your details. We start the file the same day and
            come back with every bank’s number in one message.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Button to="/contact" size="lg" arrow data-magnetic>
              Apply for financing
            </Button>
            <Button variant="ghost" size="lg" to="/home#cars">
              Browse vehicles
            </Button>
          </div>
        </div>
      </Section>
    </>
  )
}

export default Financing
